import {makeAutoObservable, observable} from 'mobx'
import {Lang} from 'yougile-lang'
import {ObjUtil} from 'yougile-util'
import {DataObjectId, GroupChat, GroupChatUsers} from 'yougile-model'
import {EventBus} from '@client/event-bus'
import {openChatEvent} from '@client/events'
import {GroupChatsClient} from '@client/group-chats-client'
import {Sync} from '@client/sync'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {showDeleteConfirm} from '@components/common/base/notifications'
import {TableUserInfo} from '@components/common/users-table/users-table'
import {UsersTableUtil} from '@components/common/users-table/users-table-util'
import {MessageBox} from '@components/message-box'
import {sortWithFunc} from '@components/project-participants/helpers'
import {createUseStore} from '@containers/hooks'
import {ChatUtils} from '@extensions/chat-utils'
import {globals} from '@extensions/globals-base'
import {UserUtils} from '@extensions/user-utils'

interface State {
  newChat: boolean
  open: boolean
  title: string
  chosenUsers: DataObjectId[]
  chatId: DataObjectId
  users: TableUserInfo[]
}

export class GroupChatStore {
  state: State = {
    newChat: false,
    open: false,
    title: '',
    chosenUsers: [],
    chatId: undefined,
    users: []
  }

  constructor() {
    makeAutoObservable(this, {
      state: observable.shallow
    })
  }

  syncState(): void {
    const expanded = ChatUtils.isChatExpanded()
    const chat = ChatUtils.getChatFromCurrentUrl()
    const chatId =
      expanded && chat && GroupChat.isGroupChat(chat) ? chat.id : undefined
    this.state.chatId = chatId
    this.state.newChat = !chatId
    this.state.title = this.state.chatId ? GroupChat.getTitle(chat) : ''
    this.state.chosenUsers = this.state.chatId
      ? (ObjUtil.keys(GroupChat.getParticipants(chat)) as DataObjectId[])
      : [globals.currentUserId]
    this.getUsers()
  }

  onChange(value: string): void {
    this.state.title = value
  }

  getUsers(): void {
    const chat = ChatUtils.getChatFromCurrentUrl()
    const chosenUsers = this.state.chatId
      ? GroupChat.getParticipants(chat)
      : {[globals.currentUserId]: true}
    const box = Sync.getCurrentSandbox()
    this.state.users = UserUtils.sortByName(box.getAllUsers(false))
      .sort((a, b) =>
        sortWithFunc(u => (+!!chosenUsers[u.id] ? 'a' : 'b'), true)(a, b)
      )
      .map(x => UsersTableUtil.convertUser(box, x))
  }

  toggleUser(users: DataObjectId[]): void {
    this.state.chosenUsers = users
  }

  async save(cancel: () => void): Promise<void> {
    const userCount = this.state.chosenUsers.length
    const box = Sync.getCurrentSandbox()
    const chosenUsers: GroupChatUsers = this.state.chosenUsers.reduce(
      (a, v) => ({...a, [v]: true}),
      {}
    )
    if (this.state.chatId) {
      if (
        userCount === 0 &&
        !(await showDeleteConfirm({
          title: Lang.msg.groupChatNoUsersOnEdit,
          subtitle: Lang.msg.groupChatNoUsersOnEditHint
        }))
      ) {
        return
      }
      const chat = box.getHub(this.state.chatId)
      if (chat) {
        GroupChat.setTitleWithoutCommit(chat, this.state.title)
        GroupChat.setParticipantsWithoutCommit(chat, chosenUsers)
        chat.commit()
        cancel()
        void Tracking.send(TrackingEvent.groupChatEditSave)
      }
      return
    }

    if (userCount === 0 || this.state.title.length === 0) {
      MessageBox.warn(Lang.msg.groupChatEditor.warning)
      return
    }
    const id = GroupChatsClient.createChat(box, chosenUsers, this.state.title)
    void Tracking.send(TrackingEvent.groupChatCreate)
    EventBus.dispatch(openChatEvent(id))
  }
}

const [GroupChatContext, useGroupChatStore] = createUseStore(GroupChatStore)
export {GroupChatContext, useGroupChatStore}
